import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Card } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Label } from "../components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { DollarSign, CheckCircle2, Clock, TrendingUp, Calendar, FileText, BarChart3, Users, Download, Check } from "lucide-react";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import AnaliseComissoesModal from "../components/AnaliseComissoesModal";
import PosvendaComissoes from "./PosvendaComissoes";

const MESES = ["Jan","Fev","Mar","Abr","Mai","Jun","Jul","Ago","Set","Out","Nov","Dez"];
const TIPO_LABEL = { "12_meses": "12 meses", mensal: "Mensal", renovacao: "Renovação", venda: "Venda" };

const fmtMoney = (v) =>
  new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(v || 0);

const fmtData = (iso) => {
  if (!iso) return "—";
  try {
    if (iso.length === 10) return new Date(iso + "T12:00:00").toLocaleDateString("pt-BR");
    return new Date(iso).toLocaleDateString("pt-BR");
  } catch { return iso; }
};

export default function MinhasComissoes() {
  const { user } = useAuth();
  const hoje = new Date();
  const [mes, setMes] = useState(hoje.getMonth() + 1);
  const [ano, setAno] = useState(hoje.getFullYear());
  const [periodo, setPeriodo] = useState("mes"); // mes | trimestre | ano
  const [vendedorFiltro, setVendedorFiltro] = useState(""); // filtro por vendedor (admin)
  const [vendedores, setVendedores] = useState([]);
  const [data, setData] = useState(null);
  const [analiseOpen, setAnaliseOpen] = useState(false);
  const [pagandoId, setPagandoId] = useState(null);

  const canFiltrarVendedor = user && ["diretor", "supervisao", "gerente", "financeiro"].includes(user.role);
  const canMarcarPago = user && ["diretor", "supervisao", "financeiro"].includes(user.role);

  // Carrega lista de vendedores (apenas para diretor/sup/gerente)
  useEffect(() => {
    if (!canFiltrarVendedor) return;
    api.get("/comercial/vendedores").then((r) => setVendedores(r.data || [])).catch(() => {});
  }, [canFiltrarVendedor]);

  // Recarrega extrato sempre que filtros mudam
  const recarregar = () => {
    const params = { mes, ano };
    if (vendedorFiltro) params.vendedor_id = vendedorFiltro;
    api.get("/comercial/comissoes/extrato", { params }).then((r) => setData(r.data));
  };

  useEffect(() => { recarregar(); }, [mes, ano, vendedorFiltro]);

  // Quem pode acessar análise consolidada
  const podeAnalisar = user && ["financeiro", "diretor", "supervisao", "gerente"].includes(user.role);

  const abrirMeuPdf = () => {
    const targetId = vendedorFiltro || user?.id;
    if (!targetId) return;
    const base = process.env.REACT_APP_BACKEND_URL;
    const token = localStorage.getItem("lab_token");
    fetch(`${base}/api/comercial/comissoes/${targetId}/pdf?mes=${mes}&ano=${ano}&formato=pdf`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => r.blob())
      .then((blob) => window.open(URL.createObjectURL(blob), "_blank"));
  };

  const exportCSV = () => {
    if (!data?.contratos?.length) {
      toast.info("Sem dados para exportar");
      return;
    }
    const headers = ["Cliente", "Tipo", "Valor 1ª Fatura", "% Bônus", "Valor Bônus", "Previsão Pgto", "Status", "Data Pgto"];
    const rows = data.contratos.map((c) => [
      c.cliente_nome,
      TIPO_LABEL[c.tipo] || c.tipo,
      (c.valor_primeira_fatura || 0).toFixed(2),
      c.percentual_bonus,
      (c.valor_bonus || 0).toFixed(2),
      c.previsao_pagamento || "",
      c.status_pagamento || "",
      c.data_pagamento || "",
    ]);
    const csv = [headers, ...rows].map((r) => r.map((v) => `"${String(v).replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob(["\ufeff" + csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `comissoes-${ano}-${String(mes).padStart(2, "0")}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success("CSV exportado");
  };

  const marcarPago = async (c) => {
    if (!canMarcarPago) return;
    if (!window.confirm(`Marcar comissão de ${c.cliente_nome} (${fmtMoney(c.valor_bonus)}) como PAGA?`)) return;
    setPagandoId(c.id);
    try {
      await api.post(`/financeiro/comercial/contratos/${c.id}/marcar-pago`);
      toast.success("Comissão marcada como paga");
      recarregar();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha ao marcar como pago");
    } finally {
      setPagandoId(null);
    }
  };

  return (
    <div className="space-y-5" data-testid="minhas-comissoes-page">
      <div className="flex items-center gap-3">
        <DollarSign size={28} style={{ color: "#1B3A6B" }} />
        <div>
          <h1 className="text-3xl font-display font-bold" style={{ color: "#1B3A6B" }}>Minhas Comissões</h1>
          <p className="text-sm text-muted-foreground">Comercial (contratos) + Pós-venda (3% sobre OS)</p>
        </div>
      </div>

      <Tabs defaultValue="comercial" className="space-y-4" data-testid="comissoes-tabs">
        <TabsList>
          <TabsTrigger value="comercial" data-testid="tab-comercial">Comercial (contratos)</TabsTrigger>
          <TabsTrigger value="posvenda" data-testid="tab-posvenda">Pós-venda 3%</TabsTrigger>
        </TabsList>

        <TabsContent value="comercial" className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="text-sm text-muted-foreground">Extrato transparente · {MESES[mes - 1]}/{ano}</div>
        <div className="flex gap-2 items-end flex-wrap">
          {podeAnalisar && (
            <Button
              onClick={() => setAnaliseOpen(true)}
              className="bg-orange-600 hover:bg-orange-700 text-white"
              data-testid="btn-analisar-pagamento"
            >
              <BarChart3 size={16} className="mr-1" /> Analisar Pagamento por Vendedor
            </Button>
          )}
          <Button variant="outline" onClick={abrirMeuPdf} data-testid="btn-meu-pdf">
            <FileText size={16} className="mr-1" /> Visualizar PDF
          </Button>
          <Button variant="outline" onClick={exportCSV} data-testid="btn-export-csv">
            <Download size={16} className="mr-1" /> Exportar CSV
          </Button>
          {canFiltrarVendedor && (
            <div>
              <Label className="text-xs flex items-center gap-1"><Users size={11} /> Vendedor</Label>
              <Select value={vendedorFiltro || "_todos"} onValueChange={(v) => setVendedorFiltro(v === "_todos" ? "" : v)}>
                <SelectTrigger className="w-[200px]" data-testid="select-vendedor"><SelectValue placeholder="Todos" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="_todos">Eu mesmo (visão padrão)</SelectItem>
                  {vendedores.map((v) => (
                    <SelectItem key={v.id} value={v.id}>{v.nome || v.email}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
          <div>
            <Label className="text-xs">Mês</Label>
            <Select value={String(mes)} onValueChange={(v) => setMes(Number(v))}>
              <SelectTrigger className="w-[120px]"><SelectValue /></SelectTrigger>
              <SelectContent>{MESES.map((m, i) => <SelectItem key={i + 1} value={String(i + 1)}>{m}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Ano</Label>
            <Select value={String(ano)} onValueChange={(v) => setAno(Number(v))}>
              <SelectTrigger className="w-[100px]"><SelectValue /></SelectTrigger>
              <SelectContent>
                {[hoje.getFullYear() - 1, hoje.getFullYear(), hoje.getFullYear() + 1].map((a) => (
                  <SelectItem key={a} value={String(a)}>{a}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>

      {data && (
        <>
          {/* Resumo */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            <Card className="p-4" data-testid="resumo-contratos-mes">
              <div className="flex items-center gap-2 text-muted-foreground text-xs uppercase mb-1">
                <CheckCircle2 size={14} /> Contratos do mês
              </div>
              <div className="text-3xl font-bold" style={{ color: "#1B3A6B" }}>{data.resumo.total_contratos_mes}</div>
            </Card>
            <Card className="p-4" data-testid="resumo-total-mes">
              <div className="flex items-center gap-2 text-muted-foreground text-xs uppercase mb-1">
                <DollarSign size={14} /> Total do mês
              </div>
              <div className="text-2xl font-bold" style={{ color: "#1B3A6B" }}>{fmtMoney(data.resumo.total_bonus_mes)}</div>
            </Card>
            <Card className="p-4" data-testid="resumo-pago">
              <div className="flex items-center gap-2 text-green-700 text-xs uppercase mb-1">
                <CheckCircle2 size={14} /> Já pago
              </div>
              <div className="text-2xl font-bold text-green-700">{fmtMoney(data.resumo.pago_mes)}</div>
            </Card>
            <Card className="p-4" data-testid="resumo-pendente">
              <div className="flex items-center gap-2 text-amber-700 text-xs uppercase mb-1">
                <Clock size={14} /> A receber
              </div>
              <div className="text-2xl font-bold text-amber-700">{fmtMoney(data.resumo.pendente_mes)}</div>
            </Card>
            <Card className="p-4" data-testid="resumo-ano">
              <div className="flex items-center gap-2 text-muted-foreground text-xs uppercase mb-1">
                <TrendingUp size={14} /> Acumulado {ano}
              </div>
              <div className="text-2xl font-bold" style={{ color: "#F97316" }}>{fmtMoney(data.resumo.total_bonus_ano)}</div>
            </Card>
          </div>

          <Card className="p-4">
            <div className="flex items-center gap-2 text-sm">
              <Calendar size={16} style={{ color: "#1B3A6B" }} />
              <span>Previsão de pagamento das comissões deste mês:</span>
              <strong style={{ color: "#1B3A6B" }}>{fmtData(data.resumo.previsao_pagamento)}</strong>
              <span className="text-xs text-muted-foreground ml-auto">
                (Regra: contratos ativados de 1 a 30 do mês → pagamento dia 20 do mês seguinte)
              </span>
            </div>
          </Card>

          {/* Tabela */}
          <Card>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Cliente</TableHead>
                  <TableHead>Tipo</TableHead>
                  <TableHead className="text-right">Valor 1ª Fatura</TableHead>
                  <TableHead className="text-center">% Bônus</TableHead>
                  <TableHead className="text-right">Valor Bônus</TableHead>
                  <TableHead>Previsão Pgto</TableHead>
                  <TableHead>Status</TableHead>
                  {canMarcarPago && <TableHead className="text-center w-32">Ações</TableHead>}
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.contratos.map((c) => (
                  <TableRow key={c.id} data-testid={`comissao-${c.id}`}>
                    <TableCell className="font-medium">{c.cliente_nome}</TableCell>
                    <TableCell><Badge variant="outline">{TIPO_LABEL[c.tipo] || c.tipo}</Badge></TableCell>
                    <TableCell className="text-right">{fmtMoney(c.valor_primeira_fatura)}</TableCell>
                    <TableCell className="text-center">{c.percentual_bonus}%</TableCell>
                    <TableCell className="text-right font-bold" style={{ color: "#1B3A6B" }}>{fmtMoney(c.valor_bonus)}</TableCell>
                    <TableCell>{fmtData(c.previsao_pagamento)}</TableCell>
                    <TableCell>
                      {c.status_pagamento === "pago" ? (
                        <Badge className="bg-green-600 text-white" data-testid={`status-pago-${c.id}`}>
                          ✅ Pago em {fmtData(c.data_pagamento)}
                        </Badge>
                      ) : (
                        <Badge className="bg-blue-600 text-white">A receber</Badge>
                      )}
                    </TableCell>
                    {canMarcarPago && (
                      <TableCell className="text-center">
                        {c.status_pagamento !== "pago" && (
                          <Button
                            size="sm"
                            variant="outline"
                            disabled={pagandoId === c.id}
                            onClick={() => marcarPago(c)}
                            data-testid={`marcar-pago-${c.id}`}
                            className="h-7 text-xs"
                          >
                            <Check size={12} className="mr-1" /> Marcar pago
                          </Button>
                        )}
                      </TableCell>
                    )}
                  </TableRow>
                ))}
                {data.contratos.length === 0 && (
                  <TableRow><TableCell colSpan={canMarcarPago ? 8 : 7} className="text-center py-6 text-muted-foreground">Nenhum contrato ativado neste mês.</TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          </Card>

          <p className="text-xs text-muted-foreground">
            * Você visualiza apenas os seus contratos. Status e valores são geridos pelo Financeiro — você não pode editá-los.
          </p>
        </>
      )}
        </TabsContent>

        <TabsContent value="posvenda" className="space-y-5">
          <PosvendaComissoes embedded={true} />
        </TabsContent>
      </Tabs>

      <AnaliseComissoesModal open={analiseOpen} onClose={() => setAnaliseOpen(false)} />
    </div>
  );
}
